import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { Eye, Printer } from 'lucide-react';
import { getPost, getRelatedPosts, getPostViews, getAdjacentPosts, getSeriesDetail } from '@/features/blog/services/blogApi';
import type { Post, Series } from '@/shared/types/api';
import CommentSection from '@/features/blog/components/CommentSection';
import TableOfContents from '@/features/blog/components/TableOfContents';
import { slugify } from '@/features/blog/utils/toc';
import ShareButtons from '@/features/blog/components/ShareButtons';
import CodeBlock from '@/features/blog/components/CodeBlock';
import PostReactions from '@/features/blog/components/PostReactions';
import { useThemeStore } from '@/shared/stores/themeStore';
import { readingTime } from '@/shared/utils/readingTime';
import Skeleton from '@/shared/components/Skeleton';
import Breadcrumbs from '@/shared/components/Breadcrumbs';

export default function PostPage() {
  const { id } = useParams<{ id: string }>();
  const postId = Number(id);
  const theme = useThemeStore((s) => s.theme);

  const { data: post, isLoading, error } = useQuery<Post>({
    queryKey: ['post', postId],
    queryFn: () => getPost(postId),
    enabled: !!postId,
  });

  const { data: relatedPosts } = useQuery<Post[]>({
    queryKey: ['relatedPosts', postId],
    queryFn: () => getRelatedPosts(postId),
    enabled: !!postId && !!post,
  });

  const { data: viewData } = useQuery<{ views: number }>({
    queryKey: ['postViews', postId],
    queryFn: () => getPostViews(postId),
    enabled: !!postId,
  });

  const { data: adjacentData } = useQuery<{ prev: Post | null; next: Post | null }>({
    queryKey: ['adjacentPosts', postId],
    queryFn: () => getAdjacentPosts(postId),
    enabled: !!postId && !!post,
  });

  const { data: seriesData } = useQuery<{ series: Series; posts: Post[] }>({
    queryKey: ['series', post?.series_id],
    queryFn: () => getSeriesDetail(post!.series_id!),
    enabled: !!post?.series_id,
  });

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-3/4 mb-3" />
        <Skeleton className="h-4 w-1/3 mb-6" />
        <Skeleton className="h-px w-full mb-6" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-5/6 mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3 mb-2" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <>
        <Helmet><title>Post Not Found — Vladyslav Chebotarov</title></Helmet>
        <p className="text-muted text-sm">Post not found.</p>
      </>
    );
  }

  const date = post.published_at ?? post.created_at;
  const minutes = readingTime(post.content_markdown ?? '');
  const description = post.excerpt || (post.content_markdown ?? '').slice(0, 160).replace(/[#*_`\n]/g, '');
  const url = `https://codder.cc/blog/${post.id}/${post.slug}`;
  const ogImageUrl = post.og_image
    ? (post.og_image.startsWith('http') ? post.og_image : `https://codder.cc${post.og_image}`)
    : null;

  // Find prev/next within series
  const seriesPosts = seriesData?.posts ?? [];
  const seriesIndex = seriesPosts.findIndex((p) => p.id === post.id);
  const seriesPrev = seriesIndex > 0 ? seriesPosts[seriesIndex - 1] : null;
  const seriesNext = seriesIndex >= 0 && seriesIndex < seriesPosts.length - 1 ? seriesPosts[seriesIndex + 1] : null;

  return (
    <article>
      <Helmet>
        <title>{post.title} — Vladyslav Chebotarov</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Vladyslav Chebotarov" />
        {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
        <meta name="twitter:card" content={ogImageUrl ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description,
            ...(ogImageUrl ? { image: ogImageUrl } : {}),
            datePublished: post.published_at ?? post.created_at,
            dateModified: post.updated_at,
            author: { '@type': 'Person', name: 'Vladyslav Chebotarov' },
            url,
          })}
        </script>
      </Helmet>

      <Breadcrumbs items={[{ label: 'Blog', to: '/blog' }, { label: post.title }]} />

      {/* Series banner */}
      {post.series_name && post.series_id && (
        <div className="text-sm mb-4 px-3 py-2 rounded" style={{ border: '1px solid var(--color-border)', backgroundColor: 'rgba(245,245,245,0.03)' }}>
          Part {post.series_order ?? '?'} of{' '}
          <Link to={`/series/${post.series_id}/${post.series_slug}`} className="text-accent hover:underline font-bold">
            {post.series_name}
          </Link>
        </div>
      )}

      <header className="mb-6">
        <h1 className="text-xl font-bold mb-2 text-accent post-title-decoration">
          {post.title}
        </h1>
        <div className="text-sm text-muted mt-3 flex flex-wrap items-center gap-x-1">
          <span>{format(new Date(date), 'yyyy.MM.dd')} :: Vladyslav Chebotarov :: {minutes} min read</span>
          {viewData && viewData.views > 0 && (
            <span className="flex items-center gap-1 ml-2">
              <Eye size={13} />
              {viewData.views} views
            </span>
          )}
          {post.tags.length > 0 && (
            <span className="ml-3">
              {post.tags.map((tag, i) => (
                <span key={tag.id}>
                  {i > 0 && ' '}
                  <Link to={`/tag/${tag.id}/${tag.slug}`} className="text-accent hover:underline">
                    #{tag.name}
                  </Link>
                </span>
              ))}
            </span>
          )}
          <button
            onClick={() => window.print()}
            className="ml-2 bg-transparent border-none cursor-pointer text-muted hover:text-accent no-print flex items-center gap-1"
            aria-label="Print this post"
            title="Print"
          >
            <Printer size={13} />
          </button>
        </div>

        <ShareButtons url={url} title={post.title} />
      </header>

      <TableOfContents markdown={post.content_markdown ?? ''} />

      <hr />

      <div className={`prose max-w-none post-content my-6${theme === 'dark' ? ' prose-invert' : ''}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight, rehypeRaw]}
          components={{
            img: ({ alt, ...props }) => <img {...props} alt={alt || ''} loading="lazy" />,
            pre: ({ children, ...props }) => <CodeBlock {...props}>{children}</CodeBlock>,
            h2: ({ children, ...props }) => {
              const text = String(children).replace(/[*_`[\]]/g, '').trim();
              return <h2 id={slugify(text)} {...props}>{children}</h2>;
            },
            h3: ({ children, ...props }) => {
              const text = String(children).replace(/[*_`[\]]/g, '').trim();
              return <h3 id={slugify(text)} {...props}>{children}</h3>;
            },
          }}
        >
          {post.content_markdown ?? ''}
        </ReactMarkdown>
      </div>

      <div className="my-6">
        <PostReactions postId={postId} />
      </div>

      <CommentSection postId={postId} />

      {/* Series Prev/Next Navigation */}
      {seriesData && (seriesPrev || seriesNext) && (
        <nav className="mt-8 pt-6 flex justify-between text-sm" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div>
            {seriesPrev && (
              <Link to={`/blog/${seriesPrev.id}/${seriesPrev.slug}`} className="text-accent hover:underline">
                &larr; Part {seriesPrev.series_order}: {seriesPrev.title}
              </Link>
            )}
          </div>
          <div className="text-right">
            {seriesNext && (
              <Link to={`/blog/${seriesNext.id}/${seriesNext.slug}`} className="text-accent hover:underline">
                Part {seriesNext.series_order}: {seriesNext.title} &rarr;
              </Link>
            )}
          </div>
        </nav>
      )}

      {/* Prev/Next Post Navigation */}
      {adjacentData && (adjacentData.prev || adjacentData.next) && (
        <nav className="mt-8 pt-6 flex justify-between text-sm" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div>
            {adjacentData.prev && (
              <Link to={`/blog/${adjacentData.prev.id}/${adjacentData.prev.slug}`} className="text-accent hover:underline">
                &larr; {adjacentData.prev.title}
              </Link>
            )}
          </div>
          <div className="text-right">
            {adjacentData.next && (
              <Link to={`/blog/${adjacentData.next.id}/${adjacentData.next.slug}`} className="text-accent hover:underline">
                {adjacentData.next.title} &rarr;
              </Link>
            )}
          </div>
        </nav>
      )}

      {relatedPosts && relatedPosts.length > 0 && (
        <section className="mt-8">
          <hr />
          <h3 className="text-base font-bold mt-6 mb-4">Related Posts</h3>
          <ul className="list-none p-0 m-0 space-y-2">
            {relatedPosts.slice(0, 3).map((rp) => (
              <li key={rp.id}>
                <Link to={`/blog/${rp.id}/${rp.slug}`} className="text-sm text-accent hover:underline">
                  {rp.title}
                </Link>
                <span className="text-xs text-muted ml-2">
                  {format(new Date(rp.published_at ?? rp.created_at), 'yyyy.MM.dd')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
